import { useCallback } from 'react';
import { useToast } from '../components/UI/ToastContainer';

/**
 * Hook for providing success feedback for phonebook operations
 */
export const useSuccessFeedback = () => {
  const { showSuccess, showInfo } = useToast();

  const showContactAdded = useCallback((contactName: string) => {
    showSuccess(
      'Contact Added',
      `${contactName} has been successfully added to your phonebook.`,
      4000
    );
  }, [showSuccess]);

  const showContactUpdated = useCallback((contactName: string) => {
    showSuccess(
      'Contact Updated',
      `${contactName} has been successfully updated.`,
      4000
    );
  }, [showSuccess]);

  const showContactDeleted = useCallback((contactName: string) => {
    showSuccess(
      'Contact Deleted',
      `${contactName} has been removed from your phonebook.`,
      4000
    );
  }, [showSuccess]);

  const showContactsImported = useCallback((count: number, duplicates?: number, failed?: number) => {
    let message = `${count} contact${count !== 1 ? 's' : ''} imported successfully.`;
    
    if (duplicates && duplicates > 0) {
      message += ` ${duplicates} duplicate${duplicates !== 1 ? 's' : ''} skipped.`;
    }
    
    if (failed && failed > 0) {
      message += ` ${failed} contact${failed !== 1 ? 's' : ''} failed to import.`;
    }

    showSuccess(
      'Import Complete',
      message,
      6000
    );
  }, [showSuccess]);

  const showContactsExported = useCallback((count: number, format: 'csv' | 'excel') => {
    const formatName = format === 'excel' ? 'Excel' : 'CSV';
    showSuccess(
      'Export Complete',
      `${count} contact${count !== 1 ? 's' : ''} exported to ${formatName} format.`,
      4000
    );
  }, [showSuccess]);

  const showCategoryCreated = useCallback((categoryName: string) => {
    showSuccess(
      'Category Created',
      `"${categoryName}" category has been created.`,
      4000
    );
  }, [showSuccess]);

  const showCategoryUpdated = useCallback((oldName: string, newName: string) => {
    showSuccess(
      'Category Updated',
      `Category renamed from "${oldName}" to "${newName}".`,
      4000
    );
  }, [showSuccess]);

  const showCategoryDeleted = useCallback((categoryName: string, contactCount: number) => {
    let message = `"${categoryName}" category has been deleted.`;
    
    if (contactCount > 0) {
      message += ` ${contactCount} contact${contactCount !== 1 ? 's' : ''} moved to General category.`;
    }

    showSuccess(
      'Category Deleted',
      message,
      5000
    );
  }, [showSuccess]);

  const showDataSynced = useCallback(() => {
    showInfo(
      'Data Synced',
      'Your phonebook data has been synchronized.',
      3000
    );
  }, [showInfo]);

  const showOfflineMode = useCallback(() => {
    showInfo(
      'Offline Mode',
      'Working offline. Changes will sync when connection is restored.',
      0 // Don't auto-dismiss
    );
  }, [showInfo]);

  const showBackOnline = useCallback(() => {
    showSuccess(
      'Back Online',
      'Connection restored. Syncing your changes...',
      4000
    );
  }, [showSuccess]);

  return {
    showContactAdded,
    showContactUpdated,
    showContactDeleted,
    showContactsImported,
    showContactsExported,
    showCategoryCreated,
    showCategoryUpdated,
    showCategoryDeleted,
    showDataSynced,
    showOfflineMode,
    showBackOnline
  };
};